// OneDrive:IgnoreCodeCoverage

// tslint:disable-next-line:no-implicit-dependencies
import * as Aria from 'aria';
import { AccountType } from "../logging/EventBase";
import IClonedEvent from "../logging/IClonedEvent";
import { Manager } from "../logging/Manager";
import { Beacon, IBeaconStartSchema } from "../logging/events/Beacon.event";
import ErrorHelper from "../logging/ErrorHelper";
import PlatformDetection from '../browser/PlatformDetection';
import { DataCompletenessInfo } from '../logging/DataCompletenessInfo';
import { SimpleUri } from '../uri/SimpleUri';
import { IDataCompletenessContext } from '../logging/DataCompletenessInfo';
import { AriaTelemetryHandler, IAriaEventContext } from '@ms/telemetry-addon-aria/lib/AriaTelemetryHandler';
import CancelablePromise from '../async/Promise';
import Features from '../features/Features';
import { Killswitch } from '../killswitch/Killswitch';
import { IAriaWebJsLogConfiguration } from '@ms/utilities-ariawebjs-wrapper/lib/index';

const ARIA_QOS_NAME = "AriaBeacon";

export interface IContextData {
    accountType: AccountType;
    completenessCallbackEndpoint?: string;
    disableCookies?: boolean;
    environment?: string;
    farmLabel?: string;
    forceEnabled?: boolean;
    isAuthenticated: boolean;
    market: string;
    manifest: string;
    session: string;
    siteSubscriptionId?: string;
    userId: string;
    version: string;
    workload?: string;
    webId?: string;
    siteId?: string;
    webTemplateId?: string;
}

interface ILoggerContext extends IAriaEventContext {
    [key: string]: string | number | boolean | undefined;
    AccountType: string;
    Environment: string;
    BrowserName: string;
    BrowserMajVer: number;
    BrowserMinVer: string;
    BrowserUserAgent: string;
    BrowserIsMobile: boolean;
    FarmLabel?: string;
    IsAuthenticated: 1 | 0;
    SiteSubscriptionId: string;
    Workload: string;
}

let ariaTelemetry: typeof Aria;

let initialized = false;

const config = {
    logStartEvents: false,
    Init: init,
    isInitialized: isInitialized
};

const EnableAriaLocalStorage = { ODB: 1355 };

export default config;

function isInitialized(aria: typeof Aria): boolean {
    return initialized;
}

function init(
    this: void,
    tenantToken: string,
    context: IContextData,
    aria: typeof Aria,
    dependencies: {
        platformDetection?: PlatformDetection;
    } = {}): () => void {
    const {
        platformDetection = new PlatformDetection()
    } = dependencies;

    let result: () => void;
    try {
        ariaTelemetry = aria;
        config.logStartEvents = true;

        const loggerContext: ILoggerContext = {
            AccountType: AccountType[context.accountType],
            BrowserIsMobile: platformDetection.isMobile,
            BrowserMajVer: platformDetection.browserMajor,
            BrowserMinVer: platformDetection.browserMinor,
            BrowserName: platformDetection.browserName,
            BrowserUserAgent: platformDetection.userAgent,
            Environment: context.environment,
            FarmLabel: context.farmLabel,
            IsAuthenticated: context.isAuthenticated ? 1 : 0,
            Market: context.market,
            Manifest: context.manifest,
            OSName: platformDetection.osName,
            OSVersion: platformDetection.osVersion,
            Session: context.session,
            SiteSubscriptionId: context.siteSubscriptionId || "",
            UserId: context.userId,
            Version: context.version,
            Workload: context.workload,
            SiteId: context.siteId,
            WebId: context.webId,
            WebTemplateId: context.webTemplateId
        };

        if (context.farmLabel) {
            loggerContext.FarmLabel = context.farmLabel;
        }

        const completenessUri = new SimpleUri(context.completenessCallbackEndpoint);
        const completenessContext: IDataCompletenessContext = {
            telemetryTenantToken: tenantToken,
            appVersion: context.version,
            environment: context.environment,
            workload: context.workload,
            farmLabel: context.farmLabel,
            callbackEndpoint: completenessUri
        };

        const enableAriaLocalStorage = platformDetection.isMagellan &&
            !Killswitch.isActivated('6B850B5CF-639F-4BFA-8923-2D8376B82197', '2/18/2019', 'Enable Aria Local Storage') &&
            Features.isFeatureEnabled(EnableAriaLocalStorage);

        const ariaLogConfiguration: IAriaWebJsLogConfiguration = {
            enableLocalStorage: enableAriaLocalStorage
        };

        const ariaTelemetryHandler = new AriaTelemetryHandler({
            ariaLoader: () => CancelablePromise.resolve(ariaTelemetry),
            context: loggerContext,
            tenantToken: tenantToken,
            disableCookies: context.disableCookies,
            logStartEvents: config.logStartEvents,
            ariaQos: {
                isQosEvent: (event: IClonedEvent) => {
                    return Beacon.isTypeOf(event) &&
                        !!event.data &&
                        (event.data as IBeaconStartSchema).name === ARIA_QOS_NAME;
                },
                reportQos: (params: {
                    result: Aria._sender.CallbackEventType;
                    statusCode: number;
                    events: Aria.datamodels.Record[];
                }) => {
                    const {
                        result: isSuccess,
                        statusCode,
                        events
                    } = params;

                    new Beacon({
                        name: ARIA_QOS_NAME,
                        retryCount: 0,
                        totalRetries: 0,
                        eventCount: events ? events.length : 0
                    }).end({
                        success: isSuccess === 0,
                        status: statusCode + ''
                    });

                    if (typeof DEBUG !== 'undefined' && DEBUG) {
                        // Display errors if the aria logger is failing to log
                        if (isSuccess !== 0) {
                            ErrorHelper.log(new Error(`Aria logger failed with status code ${statusCode}`));
                        }
                    }
                    // If completeness Url is provided, send event to completeness endpoint
                    if (completenessUri.authority !== '') {
                        new DataCompletenessInfo(completenessContext, isSuccess, statusCode, new Date().getTime(), events).send();
                    }
                }
            },
            ariaLogConfiguration
        });

        const logHandlerRemover: () => void = ariaTelemetryHandler.attach(Manager);
        initialized = true;

        result = () => {
            logHandlerRemover();
            initialized = false;
        };
    } catch (e) {
        if (e instanceof ariaTelemetry.Exception) {
            e = new Error(`Aria error: ${e.toString()}`);
        }

        ErrorHelper.log(e);
    }

    return result;
}
